import { useState } from 'react';
import { Button, HelpTip, containerClass, cn, formatDate } from '@dataroom/shared';
import { useTenantPlan } from '@/api/hooks';
import { TRIAL_CHECKLIST_MESSAGE } from '@/lib/checklist';
import { trialDaysLeft } from '@/lib/plan';
import { UpgradePlanModal } from './UpgradePlanModal';

/**
 * A slim, non-blocking banner shown app-wide (in Layout) while the tenant is on
 * a free trial: days remaining (plus, on wide screens, what the trial includes —
 * the standard checklist or your own, not the ClosingVault one; ADR-0044), or "ended" once
 * past `trialEndsAt` (with the date its rooms move to Trash, ADR-0055), with an
 * Upgrade button. Renders nothing when not on a
 * trial, while loading, or if the plan can't be read (e.g. a non-admin's 403) — it must never get in the way.
 */
export function TrialBanner() {
  const plan = useTenantPlan();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  if (!plan.data?.isTrial) return null;

  const daysLeft = plan.data.trialEndsAt ? trialDaysLeft(plan.data.trialEndsAt) : null;
  const ended = daysLeft !== null && daysLeft <= 0;
  // Below 640px the banner shortens to "12 days left" so it never wraps.
  // ADR-0055 §3: an unconverted trial's rooms move to Trash 30 days after it ends.
  const trashOn = plan.data.trialRoomsTrashAt ? formatDate(plan.data.trialRoomsTrashAt) : null;
  const message = ended ? (
    <>
      Your free trial has ended — upgrade to continue.
      {trashOn ? <> Your rooms move to Trash on {trashOn}.</> : null}
    </>
  ) : daysLeft === null ? (
    'Free trial'
  ) : (
    <>
      <span className="hidden sm:inline">Free trial · </span>
      {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
      {/* ADR-0044: what the trial includes, on screens wide enough for it. */}
      <span className="hidden lg:inline"> · {TRIAL_CHECKLIST_MESSAGE}</span>
    </>
  );

  return (
    <div
      role="status"
      className={`border-b ${ended ? 'bg-danger-soft text-danger' : 'bg-brass-soft text-ink'}`}
    >
      <div className={cn(containerClass, 'flex min-h-10 items-center justify-between gap-3 py-1 text-[13px]')}>
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span>{message}</span>
          <HelpTip id="admin.trial.banner" />
        </span>
        <Button size="sm" variant={ended ? 'default' : 'outline'} onClick={() => setUpgradeOpen(true)}>
          Upgrade
        </Button>
      </div>
      {upgradeOpen ? (
        <UpgradePlanModal currentTier={plan.data.tier} onClose={() => setUpgradeOpen(false)} />
      ) : null}
    </div>
  );
}
